
<div *ngIf="!isLoading; else loading" class="page">

  <div class="page-row">

    <div class="page-col">

      <!-- Main header -->
      <div class="header">
        <button mat-flat-button class="goback" (click)="goBack()"><mat-icon>arrow_back</mat-icon></button>
        <h1 class="title">
          {{getCardTitle()}}
          <mat-icon (click)="updateProcedure()">edit</mat-icon>
        </h1>
        <div class="separator"></div>
        <button mat-flat-button color="primary" class="save-btn" (click)="saveAndClose()"><mat-icon>check</mat-icon> {{ 'Save and close' | translate }}</button>
      </div>

      <!-- Procedure requirements -->
      <div *ngIf="procedure.requiredSkill" class="requirements">
        <span>{{ 'Requirements' | translate }}:</span>
        <mat-chip-list>
          <mat-chip color="primary" selected>{{procedure.requiredSkill.skill.name}} ({{procedure.requiredSkill.skillLevel.name}})</mat-chip>
        </mat-chip-list>
      </div>

      <!-- Content grid -->
      <div class="grid">

        <!-- Video / AR / Step details -->
        <div class="content">

          <div class="content-preview">

            <!-- Finalizing step overlay -->
            <div *ngIf="recordingFinalization" class="overlay"></div>

            <mat-tab-group #contentTabs class="content-tabs">

              <!-- Preview video -->
              <mat-tab [label]="'Video preview' | translate">

                <!-- Video preview -->
                <div *ngIf="!isChoosingVideo" class="video-preview">

                  <!-- Unlink video button -->
                  <button *ngIf="procedure.videoUrl" class="link-video-btn unlink" mat-stroked-button (click)="unlinkVideo()">{{ 'Unlink video' | translate }}</button>

                  <!-- Show video if exists -->
                  <ng-container *ngIf="procedure.videoUrl; else noVideo">
                    <span *ngIf="recordingTaskRef" class="recording-step-info">{{ 'Recording step...' | translate }}</span>
                    <video #videoPlaybackHtml controls (loadedmetadata)="videoDuration = $event.target.duration" (timeupdate)="onVideoTimeUpdate($event.target.currentTime)">
                      <source [src]="procedure.videoUrl" type="video/mp4">
                    </video>
                  </ng-container>

                  <!-- Link video from library -->
                  <ng-template #noVideo>

                    <!-- No video banner -->
                    <div class="no-video">
                      <mat-icon>videocam_off</mat-icon>
                      {{ 'No video available' | translate }}
                    </div>

                    <!-- Link video button -->
                    <button class="link-video-btn" mat-stroked-button (click)="chooseVideo()">{{ 'Link from library' | translate }}</button>

                  </ng-template>

                </div>

                <!-- Video selection grid -->
                <div *ngIf="isChoosingVideo" class="selection-grid">

                  <ng-container *ngIf="!videoLibraryUploading">
                    <app-media-grid
                      #videoSelectionGrid
                      [equipment]="procedure.equipmentModel"
                      [rows]="3"
                      [fixDimension]="'rows'"
                      [resources]="resources"
                      [paginator]="resourcePaginator"
                      [selectable]="true"
                      [uploadableResources]="[ResourceType.VIDEO]"
                      [viewableResources]="[ResourceType.VIDEO]"
                      (onResourceSelected)="onVideoSelected($event)"
                    ></app-media-grid>
                    <div class="buttons">
                      <button mat-stroked-button class="back-btn" (click)="disposeVideoGrid()">{{ 'Cancel' | translate }}</button>
                    </div>
                  </ng-container>
                  <ng-container *ngIf="videoLibraryUploading">
                    <app-scotty-spinner text="Uploading. Please wait..." diameter="40"></app-scotty-spinner>
                  </ng-container>

                </div>

              </mat-tab>

              <!-- AR resources -->
              <mat-tab [label]="'AR resources' | translate" [disabled]="!canViewAR">

                <!-- Show global library button -->
                <button *ngIf="canViewAR && !showLibrary" mat-flat-button [disableRipple]="true" class="collapse-library-btn" (click)="showLibrary = true"><mat-icon>library_add</mat-icon> {{ 'Show library' | translate }}</button>

                <!-- Step resources grid -->
                <div *ngIf="canViewAR && !isUpdatingStep; else loading" class="selection-grid">
                  <app-media-grid
                    #stepLibrary
                    [equipment]="procedure.equipmentModel"
                    [rows]="3"
                    [fixDimension]="'rows'"
                    [resources]="stepResources"
                    [paginator]="stepResourcePaginator"
                    [selectable]="true"
                    [multiselect]="true"
                    [viewableResources]="ResourceUtils.allResources"
                  ></app-media-grid>
                  <div class="buttons">
                    <button mat-stroked-button color="warn" [disabled]="stepLibrary.selectedResources.length == 0" (click)="removeResourcesFromStep()"><mat-icon>delete</mat-icon> {{ 'Remove resource from current step' | translate }}</button>
                  </div>
                </div>

              </mat-tab>

            </mat-tab-group>

          </div>
          <div class="step-details" [class.card-highlighted]="recordingFinalization">
            <div class="container">

              <!-- Task & Step -->
              <h1 class="title">{{ 'Step details' | translate }}</h1>
              <ng-container *ngIf="selectedStep; else noStepSelected">

                <!-- Step base info -->
                <form [formGroup]="stepDetailsForm">

                  <ng-container *ngIf="procedure.videoUrl">

                    <!-- Seeks editor -->
                    <div *ngIf="isEditingSeeks" class="seek-editor">

                      <span>{{ 'From' | translate }}:</span>
                      <ngx-mat-timepicker
                        [(ngModel)]="seekStartRelativeTime"
                        (ngModelChange)="onUpdateStartSeek()"
                        [ngModelOptions]="{standalone: true}"
                        [showSpinners]="true"
                        [showSeconds]="true"
                        [stepHour]="1" [stepMinute]="1" [stepSecond]="1">
                      </ngx-mat-timepicker>

                      <span>{{ 'To' | translate }}:</span>
                      <ngx-mat-timepicker
                        [(ngModel)]="seekEndRelativeTime"
                        (ngModelChange)="onUpdateEndSeek()"
                        [ngModelOptions]="{standalone: true}"
                        [showSpinners]="true"
                        [showSeconds]="true"
                        [stepHour]="1" [stepMinute]="1" [stepSecond]="1">
                      </ngx-mat-timepicker>

                    </div>

                    <!-- Duration -->
                    <div class="step-duration">
                      <ng-container *ngIf="isEditingSeeks; else stepDuration">
                        {{ 'Total step duration' | translate }} {{getStepDuration()}} {{ 'seconds' | translate }}
                        <button mat-stroked-button (click)="isEditingSeeks = false">{{ 'Update seeks' | translate }}</button>
                      </ng-container>
                      <ng-template #stepDuration>
                        {{ 'From' | translate }} {{Utils.getTime(Utils.getTime2Hours(stepDetailsForm.value.seekStart), Utils.getTime2Minutes(stepDetailsForm.value.seekStart), Utils.getTime2Seconds(stepDetailsForm.value.seekStart))}} {{ 'to' | translate }} {{Utils.getTime(Utils.getTime2Hours(stepDetailsForm.value.seekEnd), Utils.getTime2Minutes(stepDetailsForm.value.seekEnd), Utils.getTime2Seconds(stepDetailsForm.value.seekEnd))}} ({{getStepDuration()}} {{ 'seconds' | translate }})
                        <button mat-stroked-button (click)="editSeeks()">{{ 'Edit seeks' | translate }}</button>
                      </ng-template>
                    </div>

                  </ng-container>

                  <!-- Step name -->
                  <mat-form-field>
                    <mat-label>{{ 'Name' | translate }}</mat-label>
                    <input matInput formControlName="name" required>
                    <mat-error>{{ 'Name is required' | translate }}</mat-error>
                  </mat-form-field>

                  <!-- Step description -->
                  <mat-form-field>
                    <mat-label>{{ 'Description' | translate }}</mat-label>
                    <textarea matInput formControlName="description" rows="10"></textarea>
                  </mat-form-field>

                  <div class="separator"></div>

                  <!-- Step buttons -->
                  <div class="row">
                    <button mat-stroked-button class="save-detail-btn" (click)="closeStep()">{{ (recordingFinalization ? 'Cancel' : 'Close') | translate }}</button>
                    <button *ngIf="selectedStep.id" mat-raised-button color="warn" class="save-detail-btn" (click)="removeStep(selectedStep)">{{ 'Remove step' | translate }}</button>
                    <button mat-raised-button color="primary" class="save-detail-btn" [disabled]="isEditingSeeks || stepDetailsForm.invalid" (click)="createOrUpdateStep(selectedStep)">
                      {{(selectedStep.id ? 'Update step' : 'Save step and continue') | translate}}
                    </button>
                  </div>

                </form>

              </ng-container>

              <ng-template #noStepSelected>
                <div class="no-step">{{ 'Select a step from timeline' | translate }}</div>
              </ng-template>

            </div>
          </div>

        </div>

        <!-- Timeline -->
        <div class="timeline">

          <!-- Finalizing step overlay -->
          <div *ngIf="recordingFinalization" class="overlay"></div>

          <!-- Task list -->
          <div class="task-list">


            <!-- Single task -->
            <div *ngFor="let task of procedure.tasks" class="task">

              <!-- Task title -->
              <div class="task-title" (click)="manageTask(task)">
                <span>{{ task.name }}</span>
                <mat-icon class="noselect">edit</mat-icon>
              </div>

              <!-- Step list -->
              <div class="step-list"
                   cdkDropList
                   cdkDropListOrientation="horizontal"
                   [cdkDropListData]="task.steps"
                   (cdkDropListDropped)="drop($event)">

                <!-- Single Step -->
                <div *ngFor="let step of task.steps" cdkDrag [cdkDragDisabled]="!!recordingTaskRef" [cdkDragPreviewClass]="selectedStep == step ? 'selected' : ''" [cdkDragData]="step">
                  <div class="step noselect" [class.selected]="selectedStep == step" (click)="manageStep(step)">

                    <!-- Step title -->
                    <div [matTooltip]="step.name" matTooltipPosition="below">
                      <span class="step-title">{{step.name}}</span>
                    </div>

                    <!-- From / To video seeks -->
                    <ng-container *ngIf="procedure.videoUrl">
                      <div class="from">{{ 'From' | translate }}: <span>{{getStepFromTime(step)}}</span></div>
                      <div class="to">{{ 'To' | translate }}: <span>{{getStepToTime(step)}}</span></div>
                    </ng-container>
                  </div>
                </div>

                <!-- Add new Step -->
                <div *ngIf="!recordingTaskRef" class="new-step noselect" (click)="addStep(task)">
                  {{ 'Add step' | translate }}
                  <div *ngIf="procedure.videoUrl">{{ 'at' | translate }} {{Utils.getTime(getVideoTime2Hours(), getVideoTime2Minutes(), getVideoTime2Seconds())}}</div>
                </div>

                <!-- Stop recording video -->
                <div *ngIf="recordingTaskRef == task" class="close-step noselect" (click)="stopStepRecording(task)">
                  {{ (recordingFinalization ? 'Finalizing...' : 'Close step') | translate }}
                  <div>
                    {{ 'at' | translate }} {{Utils.getTime(getVideoTime2Hours(), getVideoTime2Minutes(), getVideoTime2Seconds())}}
                    <br><br>
                    {{ 'Duration' | translate }}: {{videoDeltaTime()}} {{ 'sec' | translate }}
                  </div>
                </div>

              </div>

            </div>


            <!-- New task -->
            <div class="task">
              <div class="task-title" (click)="manageTask()">
                <span>New task</span>
                <mat-icon class="noselect">add</mat-icon>
              </div>
            </div>

          </div>

          <!--<div class="separator"></div>-->

        </div>

      </div>

    </div>

    <!-- Global resource library -->
    <div class="library" [class.show]="showLibrary">
      <ng-container *ngIf="selectedStep && !selectedStep.isNew; else loading">

        <!-- Title -->
        <h1 class="title">{{ 'Resource library' | translate }}</h1>
        <span>{{ 'Select resources to link them in a step or select multiple resources to add in a single action' | translate }}</span>
        <button mat-flat-button [disableRipple]="true" class="collapse-library-btn" (click)="showLibrary = false">{{ 'Collapse' | translate }} <mat-icon>keyboard_arrow_right</mat-icon></button>

        <!-- Library resources -->
        <app-media-grid
          #library
          style="margin-top: 15px;"
          [searchBarExpanded]="true"
          [equipment]="procedure.equipmentModel"
          [paginator]="libraryResourcePaginator"
          [resources]="libraryResources"
          [cols]="1"
          fixDimension="columns"
          [selectable]="true"
          [multiselect]="true"
          [viewableResources]="ResourceUtils.allResources"
        ></app-media-grid>

        <!-- Add resource from library -->
        <button mat-raised-button color="primary" [disabled]="library.selectedResources.length == 0" (click)="addResourcesToStep()">{{ 'Assign selected resources to current step' | translate }}</button>

      </ng-container>
    </div>

  </div>

</div>

<ng-template #loading>
  <app-scotty-spinner></app-scotty-spinner>
</ng-template>
